import { useCallback, useState, useEffect, useMemo, ReactNode } from 'react'
import * as React from 'react'
import { Link, Route, RouteComponentProps, Switch } from 'react-router-dom'
import classNames from 'classnames'
import { Formik, Form, Field, ErrorMessage, FieldProps, FormikActions, FormikProps, isNaN, FormikErrors } from 'formik'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import { Modifier } from 'react-day-picker/'
import 'react-day-picker/lib/style.css'
import { useToasts } from 'react-toast-notifications'
import { useHistory } from 'react-router-dom'
import {
  toDate,
  format as dateFormat,
  addMonths,
  addYears,
  isAfter,
  isValid,
  parse,
  addDays,
  isEqual,
  subYears,
} from 'date-fns'

import { authClient } from 'auth'

// libraries
import ReactPaginate from 'react-paginate'
import { Tooltip } from 'react-lightweight-tooltip'
import ReactModal from 'react-modal'

/* Components */
import { SubmitButton } from '../components/SubmitButton'
import { Loading } from '../components/Loading'
import FilterButton from '../components/FilterButton'
import { ImageContainer } from '../components/ImageContainer'
import ResultsPerPage from 'components/ResultsPerPage'
import { MoreInfoModalForm } from './moreInfoCuration/modalMoreInfoForm'
import { MoreInfoModalQuestion } from './moreInfoCuration/modalMoreInfoCuration'

// constants
import { ROUTES } from 'lib/constants'

// assets
import { ReactComponent as EditIcon } from 'images/edit.svg'
import sortDown from 'images/sort-down.png'
import sortUp from 'images/sort-up.png'
import infoButton from 'images/info-button.svg'

/* Helpers */
import { useAsync } from 'react-helpers'
import { PoapEventSchema, PoapEventSchemaEdit, PoapQrRequestSchema } from 'lib/schemas'
import { generateSecretCode, getErrorMessageFromCode } from 'lib/helpers'
import {
  createEvent,
  getActiveRedeemRequests,
  getEventByFancyId,
  getEventById,
  getPaginatedEvents,
  PaginatedEvent,
  PoapEvent,
  PoapFullEvent,
  postRedeemRequests,
  RedeemRequestType,
  SortCondition,
  SortDirection,
  updateEvent,
  EventFilter,
} from '../api'
import FormFilterReactSelect from 'components/FormFilterReactSelect'
import { Helmet } from 'react-helmet'
import { analyticsTracking } from 'lib/analytics/analytics'
import { INewEvent } from 'lib/analytics/interfaces/analyticsInterfaces'
import { isEmptyString } from '../lib/utils'

type EventEditValues = {
  name: string
  id: number
  description: string
  start_date: Date | number | undefined
  end_date: Date | number | undefined
  expiry_date: Date | number | undefined
  city: string
  country: string
  event_url: string
  requested_codes?: number
  image?: Blob
  isFile: boolean
  secret_code?: string | undefined
  email: string
}

// creation modal types
type QrRequestModalProps = {
  handleModalClose: () => void
  setIsActiveQrRequest: (id: number) => void
  eventId?: number
  secretCode?: string
  type: RedeemRequestType
}

type QrRequestFormikValues = {
  requested_codes: number
  secret_code: string
}

type DatePickerDay = 'start_date' | 'end_date' | 'expiry_date'

type SetFieldValue = (field: string, value: any) => void

type DatePickerContainerProps = {
  text: string
  dayToSetup: DatePickerDay
  handleDayClick: (day: Date, dayToSetup: DatePickerDay, setFieldValue: SetFieldValue) => void
  setFieldValue: SetFieldValue
  disabledDays: RangeModifier | Modifier | Modifier[] | RangeModifier[] | undefined
  placeholder?: string
  helpText?: string
  disabled: boolean
  value: string | Date
}

type PaginateAction = {
  selected: number
}

type EventTableProps = {
  events: PoapEvent[] | undefined
  limit: number
  total: number
  onChangePage: (page: number) => void
  onChangeSort: (sort: SortCondition) => void
}

type EventFieldProps = {
  title: string | ReactNode
  name: string
  placeholder?: string
  disabled?: boolean
  type?: string
  maxLength?: number
  action?: () => void
  checked?: boolean
}

export interface RangeModifier {
  from: Date
  to: Date
}

export const EventsPage = () => (
  <Switch>
    <Route exact path={ROUTES.events.path} component={EventList} />

    <Route exact path={ROUTES.eventsNew.path} component={CreateEventForm} />

    <Route exact path={`${ROUTES.event.path}${ROUTES.event.slug}`} component={EditEventForm} />
  </Switch>
)

export const CreateEventForm: React.FC = () => <EventForm create />

export const EditEventForm: React.FC<
  RouteComponentProps<{
    eventId: string
  }>
> = ({ location, match }) => {
  const [event, setEvent] = useState<null | PoapEvent>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const history = useHistory()

  useEffect(() => {
    const fn = async () => {
      setLoading(true)
      let event: null | PoapFullEvent
      if (isNaN(+match.params.eventId)) {
        try {
          event = await getEventByFancyId(match.params.eventId)
        } catch (error) {
          event = await getEventByFancyId(match.params.eventId, true)
        }
      } else {
        try {
          event = await getEventById(+match.params.eventId)
        } catch (error) {
          event = await getEventById(+match.params.eventId, true)
        }
      }
      setEvent(event)
      setLoading(false)
    }
    fn().then()
  }, [location, match])

  if (!loading) {
    if (!event) {
      history.push(ROUTES.events.path)
    } else {
      return <EventForm event={event} />
    }
  }

  return <div>Loading...</div>
}

const toValidIsoDate = (strDate: string): Date => {
  const dateRegex = /[/-]/g
  const validIsoDateString = strDate?.replace(dateRegex, ' ')
  return new Date(validIsoDateString)
}

const featureCurationModalMoreInfo: boolean = process.env.REACT_APP_SHOW_CURATION_INFO_MODAL === 'show'

const EventForm: React.FC<{ create?: boolean; event?: PoapFullEvent }> = ({ create, event }) => {
  // States
  const [virtualEvent, setVirtualEvent] = useState<boolean>(event ? event.virtual_event : false)
  const [privateEvent, setPrivateEvent] = useState<boolean>(event ? !!event.private_event : false)
  const [isQrRequestModalOpen, setIsQrRequestModalOpen] = useState<boolean>(false)
  const [isActiveQrRequest, setIsActiveQrRequest] = useState<boolean>(true)
  const [moreInfoModalQuestion, setMoreInfoModalQuestion] = useState<boolean>(false)
  const [moreInfoModalForm, setMoreInfoModalForm] = useState<boolean>(false)
  const [dropId, setDropId] = useState<string>('')
  const [isExpiryEvent, setIsExpiryEvent] = useState<boolean>(true)
  const [multiDay, setMultiDay] = useState<boolean>(event ? event.start_date !== event.end_date : false)
  const [startDate, setStartDate] = useState<Date | undefined>(
    event ? toDate(toValidIsoDate(event.start_date)) : undefined,
  )
  const [endDate, setEndDate] = useState<Date | undefined>(event ? toDate(toValidIsoDate(event.end_date)) : undefined)
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(
    event ? toDate(toValidIsoDate(event.expiry_date)) : undefined,
  )
  const [isAdmin] = useState<boolean>(authClient.isAuthenticated())
  const [secretCode, setSecretCode] = useState<string>('')
  const history = useHistory()

  const newSecretCode = useMemo(() => generateSecretCode(), [])

  useEffect(() => {
    if (isAdmin && event?.secret_code) {
      setSecretCode(String(event.secret_code))
    }
  }, [event, isAdmin])

  useEffect(() => {
    if (startDate) {
      if (!multiDay) {
        // If it's a single day event, then end date equals start date
        setEndDate(startDate)
      } else {
        if (!endDate || isEqual(startDate, endDate) || isAfter(startDate, endDate)) {
          // If it's a multi day event and startDate is equal or after endDate, then end date is moved to 1 day from the start date
          setEndDate(addDays(startDate, 1))
        }

        if (endDate && expiryDate && (isEqual(endDate, expiryDate) || isAfter(endDate, expiryDate))) {
          // If it's a multi day event and endDate is equal or after expiryDate, then expiry date is moved to 1 month from the end date
          setExpiryDate(addMonths(endDate, 1))
        }
      }

      // If the start date > expiry date, then expiry date is moved to 1 month from the start date.
      if (!expiryDate || isEqual(startDate, expiryDate) || isAfter(startDate, expiryDate)) {
        setExpiryDate(addMonths(startDate, 1))
      }

      // If doesn't have expiry date or endDate is after it, then expiry date is moved to 1 month from the start date.
      if (endDate && (!expiryDate || isAfter(endDate, expiryDate))) {
        setExpiryDate(addMonths(endDate, 1))
      }
    }
  }, [startDate, endDate, expiryDate, multiDay])

  const { addToast } = useToasts()

  /**
   * @params {string} strDate - Format: '01-Nov-2021'
   * @returns {string} validIsoDateString: Format: '01 Nov 2021'
   **/
  const convertToIsoDateFormat = (strDate: string) => {
    const dateRegex = /[/-]/g
    const validIsoDateString = strDate.replace(dateRegex, ' ')
    return validIsoDateString
  }

  const checkActiveQrRequest = async (id: number) => {
    const activeRedeemRequests = await getActiveRedeemRequests(id, RedeemRequestType.qr_code)
    setIsActiveQrRequest(activeRedeemRequests.length > 0)
  }

  const checkExpiryEvent = async (expiry_date: string) => {
    const expiryDateValidIsoFormat = convertToIsoDateFormat(expiry_date)

    const today = new Date()
    const expiry = new Date(expiryDateValidIsoFormat)

    const eventIsExpired = isAfter(today, expiry)
    setIsExpiryEvent(eventIsExpired)
  }

  useEffect(() => {
    if (event) {
      checkActiveQrRequest(event.id).then()
      checkExpiryEvent(event.expiry_date).then()
    }
  }, [event]) /* eslint-disable-line react-hooks/exhaustive-deps */

  const initialValues = () => {
    if (event) {
      let {
        virtual_event,
        secret_code,
        start_date: init_start_date,
        end_date: init_end_date,
        expiry_date: init_expiry_date,
        private_event,
        ...eventKeys
      } = event

      return {
        ...eventKeys,
        start_date: new Date(init_start_date),
        end_date: new Date(init_end_date),
        expiry_date: new Date(init_expiry_date),
        isFile: false,
        secret_code: secretCode ? secretCode.toString().padStart(6, '0') : '',
        email: '',
      }
    } else {
      const values: EventEditValues = {
        name: '',
        id: 0,
        description: '',
        start_date: 0,
        end_date: 0,
        expiry_date: 0,
        city: '',
        requested_codes: 0,
        country: '',
        event_url: '',
        image: new Blob(),
        isFile: true,
        secret_code: newSecretCode,
        email: '',
      }
      return values
    }
  } /* eslint-disable-line react-hooks/exhaustive-deps */

  const handleFileChange = (file: File, setFieldValue: SetFieldValue, name: string) => {
    // Invalid files - Clear uploader and return
    if (!file) {
      setFieldValue(name, {})
      return
    }

    setFieldValue(name, file)
  }

  const toggleVirtualEvent = () => setVirtualEvent(!virtualEvent)
  const togglePrivateEvent = () => setPrivateEvent(!privateEvent)

  const handleDayClick = (day: Date, dayToSetup: DatePickerDay) => {
    switch (dayToSetup) {
      case 'start_date':
        setStartDate(day)
        break
      case 'end_date':
        setEndDate(day)
        break
      case 'expiry_date':
        setExpiryDate(day)
        break
      default:
    }
  }

  const handleQrRequestModalRequestClose = (): void => {
    setIsQrRequestModalOpen(false)
  }

  const handleQrRequestModalClick = (): void => {
    setIsQrRequestModalOpen(true)
  }

  const warning = (
    <div key="warning" className={'backoffice-tooltip'}>
      {' '}
      {create ? (
        <>
          Be sure to save the 6 digit <b>Edit Code</b> to make any further updates
        </>
      ) : (
        <>
          Be sure to complete the 6 digit <b>Edit Code</b> that was originally used
        </>
      )}
    </div>
  )

  const editQrRequestWarning = (
    <div key="editQrRequestWarning" className={'backoffice-tooltip'}>
      Request the amount of codes you will need for this drop
    </div>
  )

  const activeQrRequestWarning = (
    <div key="activeQrRequestWarning" className={'backoffice-tooltip'}>
      {' '}
      {!isExpiryEvent ? (
        <>A request for this drop is being processed</>
      ) : (
        <>You can't request codes on an expired drop</>
      )}
    </div>
  )

  const editQrRequest = (
    <>
      <b>How many mint links do you need?</b>
      {/* @ts-ignore TODO: remove ignore TS validation */}
      <Tooltip content={[editQrRequestWarning]}>
        <img alt="Informative message" src={infoButton} className={'info-button'} />
      </Tooltip>
    </>
  )

  const editLabel = (
    <>
      <b>Edit Code</b>
      {/* @ts-ignore TODO: remove ignore TS validation */}
      <Tooltip content={[warning]}>
        <img alt="Informative message" src={infoButton} className={'info-button'} />
      </Tooltip>
    </>
  )

  type RequestCodesButtonProps = {
    isDisabled?: boolean
    onClickAction?: () => void
  }

  const RequestCodesButton: React.FC<RequestCodesButtonProps> = ({ isDisabled, onClickAction }) => {
    return (
      <button
        disabled={isDisabled}
        type="button"
        className={`filter-base filter-button ${isDisabled && 'disabled'}`}
        onClick={onClickAction}
      >
        Request more codes
      </button>
    )
  }

  return (
    <div className={'bk-container'}>
      <Formik
        initialValues={initialValues()}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={create ? PoapEventSchema : PoapEventSchemaEdit}
        onSubmit={async (submittedValues: EventEditValues, actions: FormikActions<EventEditValues>) => {
          try {
            actions.setSubmitting(true)
            const formData = new FormData()
            let { isFile, ...othersKeys } = submittedValues

            if (create && !isFile) {
              actions.setErrors({ isFile: 'An image is required' })
              actions.setSubmitting(false)
              return
            }

            if (create && !submittedValues['email']) {
              actions.setErrors({ email: 'An email is required' })
              actions.setSubmitting(false)
              return
            }

            if (!create && !isAdmin && submittedValues['image']) {
              actions.setErrors({ image: 'Can not edit the POAP image after creation' })
              actions.setSubmitting(false)
              return
            }

            // On Update - Do not send secret code if is not present
            if (!create && !submittedValues['secret_code']) {
              const { secret_code, ...rest } = othersKeys
              othersKeys = rest
            }

            Object.entries(othersKeys).forEach(([key, value]) => {
              // @ts-ignore
              formData.append(key, typeof value === 'number' ? String(value) : value)
            })

            /*
                Format all dates for retrocompatibility
                of how the dates are sent
                to the API (MM-dd-yyyy)
            */
            if (startDate && endDate && expiryDate) {
              formData.set('start_date', dateFormat(startDate, 'MM-dd-yyyy'))
              formData.set('end_date', dateFormat(endDate, 'MM-dd-yyyy'))
              formData.set('expiry_date', dateFormat(expiryDate, 'MM-dd-yyyy'))
            }

            formData.set('virtual_event', String(virtualEvent))
            formData.set('private_event', String(privateEvent))

            if (create) {
              analyticsTracking.drop.trackRequestCreateDrop()
              const dropData: INewEvent | any = await createEvent(formData!)
              analyticsTracking.drop.trackRequestedCreateDropSuccess(dropData.id)
              setMoreInfoModalQuestion(true)
              setDropId(dropData.id)
            } else if (event) {
              await updateEvent(formData!, event.fancy_id)
              addToast('Drop updated successfully', { appearance: 'success', autoDismiss: true })
            }
            actions.setSubmitting(false)

            if (!create || !featureCurationModalMoreInfo) {
              history.push(ROUTES.events.path)
            }
          } catch (e) {
            actions.setSubmitting(false)
            // @ts-expect-error TODO: err is type unknown
            const errorMessage = getErrorMessageFromCode(e.message, isAdmin)
            addToast(errorMessage, { appearance: 'error', autoDismiss: true })
          }
        }}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => {
          return (
            <>
              <Helmet>
                <title>POAP - Create Drop</title>
                <meta property="og:title" content="POAP - Create Drop" />
              </Helmet>
              <Form>
                {create ? (
                  <>
                    <h2>Create Drop</h2>
                    <EventField disabled={!create} title="What are you commemorating?" name="name" />
                  </>
                ) : (
                  <>
                    <div className="event-top-bar-container">
                      <h2 className="margin-0">
                        #{event!.id} - {event!.name} - {event!.year}
                      </h2>
                      <div className="right_content">
                        {isActiveQrRequest || isExpiryEvent ? (
                          <>
                            {/* @ts-ignore TODO: remove ignore TS validation */}
                            <Tooltip content={[activeQrRequestWarning]}>
                              <RequestCodesButton isDisabled />
                            </Tooltip>
                          </>
                        ) : (
                          <RequestCodesButton isDisabled={false} onClickAction={handleQrRequestModalClick} />
                        )}
                      </div>
                    </div>
                    <EventField disabled={false} title="What are you commemorating?" name="name" />
                    <ReactModal
                      isOpen={isQrRequestModalOpen}
                      onRequestClose={handleQrRequestModalRequestClose}
                      shouldFocusAfterRender={true}
                      shouldCloseOnEsc={true}
                      shouldCloseOnOverlayClick={true}
                      style={{ content: { overflow: 'visible' } }}
                    >
                      <QrRequestModal
                        eventId={event?.id}
                        handleModalClose={handleQrRequestModalRequestClose}
                        setIsActiveQrRequest={checkActiveQrRequest}
                        type={RedeemRequestType.qr_code}
                      />
                    </ReactModal>
                  </>
                )}
                <EventField
                  disabled={false}
                  title="What do you want people to remember about this drop?"
                  type="textarea-character-count"
                  name="description"
                  placeholder="Explain what this POAP is about, including how the POAP will be distributed.
                This text is stored on the NFT metadata and displayed in the POAP mobile app and all across the POAP ecosystem.
                Drops in languages other than English still have to provide an English description."
                />
                <CheckboxField
                  title="Virtual Drop"
                  name="virtual_event"
                  action={toggleVirtualEvent}
                  checked={virtualEvent}
                />
                <div className="bk-group">
                  <EventField
                    disabled={false}
                    title={
                      <>
                        City <i>Optional</i>
                      </>
                    }
                    name="city"
                  />
                  <EventField
                    disabled={false}
                    title={
                      <>
                        Country <i>Optional</i>
                      </>
                    }
                    name="country"
                  />
                </div>

                <CheckboxField
                  title="Multi-day Drop"
                  name="multi_day"
                  action={() => setMultiDay((prevSate) => !prevSate)}
                  checked={multiDay}
                />
                <div className="bk-group">
                  <DayPickerContainer
                    text="Start Date"
                    dayToSetup="start_date"
                    handleDayClick={handleDayClick}
                    setFieldValue={setFieldValue}
                    value={startDate ? startDate : ''}
                    disabled={false}
                    disabledDays={
                      endDate && multiDay
                        ? {
                            before: subYears(endDate, 1),
                          }
                        : []
                    }
                  />
                  <DayPickerContainer
                    text="End Date"
                    dayToSetup="end_date"
                    handleDayClick={handleDayClick}
                    setFieldValue={setFieldValue}
                    value={endDate ? endDate : ''}
                    disabled={!multiDay}
                    disabledDays={
                      startDate
                        ? {
                            before: addDays(startDate, 1),
                            // Maximum end date is one year after the event started
                            after: addYears(startDate, 1),
                          }
                        : undefined
                    }
                  />
                  <DayPickerContainer
                    text="Expiry Date"
                    dayToSetup="expiry_date"
                    handleDayClick={handleDayClick}
                    setFieldValue={setFieldValue}
                    helpText="After this date, users will no longer be able to mint this POAP"
                    value={expiryDate ? expiryDate : ''}
                    disabled={!endDate}
                    disabledDays={
                      endDate && !isAdmin
                        ? [
                            {
                              before: addDays(endDate, 1),
                            },
                            {
                              before: new Date(),
                            },
                            {
                              // Maximum expiration date is one year after the event ended
                              after: addYears(endDate, 1),
                            },
                          ]
                        : endDate && isAdmin
                        ? [
                            {
                              before: addDays(endDate, 1),
                            },
                          ]
                        : undefined
                    }
                  />
                </div>
                <div className="bk-group">
                  <EventField title="Do you have a website attendees should visit?" name="event_url" />
                </div>
                <div className="bk-group">
                  <ImageContainer
                    text="POAP artwork (cannot edit image after creation)"
                    handleFileChange={handleFileChange}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    disabled={!isAdmin && !create}
                    name="image"
                  />
                  <div>
                    <EventField disabled={false} title={editLabel} name="secret_code" />
                    {create && (
                      <div className={'email-checkbox'}>
                        <EventField disabled={false} title={'What is your email?'} name="email" />
                      </div>
                    )}
                    {!create && isAdmin && (
                      <div className={'email-checkbox'}>
                        {event && event.email && (
                          <>
                            <label>What is your email?</label>
                            <input type={'text'} disabled value={event.email} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {event && event.image_url && (
                  <div className={'image-edit-container'}>
                    <img alt={event.image_url} className={'image-edit'} src={event.image_url} />
                  </div>
                )}
                {create && <EventField title={editQrRequest} type="number" name="requested_codes" />}
                {/* @ts-ignore TODO: remove ignore TS validation */}
                <Tooltip
                  content={
                    'By checking this box, your drop will not show up in the drop list. You can remove this in the future.'
                  }
                >
                  <CheckboxField
                    title={'Private Drop*'}
                    name="private_event"
                    action={togglePrivateEvent}
                    checked={privateEvent}
                  />
                </Tooltip>
                <label className="info-label">*If this is a test drop, please make your drop private.</label>
                <SubmitButton
                  text="Save"
                  isSubmitting={isSubmitting}
                  canSubmit={true}
                  onClick={analyticsTracking.drop.trackClickSaveNewPoapButton}
                />
              </Form>
            </>
          )
        }}
      </Formik>

      {featureCurationModalMoreInfo && (
        <ReactModal
          isOpen={moreInfoModalQuestion || moreInfoModalForm}
          onRequestClose={() => {
            setMoreInfoModalQuestion(false)
            setMoreInfoModalForm(false)
            history.push(ROUTES.events.path)
          }}
          shouldFocusAfterRender={true}
          shouldCloseOnEsc={true}
          shouldCloseOnOverlayClick={true}
          style={{ content: { overflow: 'visible', marginTop: '-10vh' } }}
        >
          {moreInfoModalQuestion && (
            <MoreInfoModalQuestion
              handheCancel={() => {
                setMoreInfoModalQuestion(false)
                analyticsTracking.drop.trackMoreInfoModal(false)
                history.push(ROUTES.events.path)
              }}
              handleNext={() => {
                setMoreInfoModalQuestion(false)
                setMoreInfoModalForm(true)
                analyticsTracking.drop.trackMoreInfoModal(true)
              }}
            />
          )}

          {moreInfoModalForm && (
            <MoreInfoModalForm
              dropId={dropId}
              handleModalClose={() => setMoreInfoModalForm(false)}
              handleComplete={() => history.push(ROUTES.events.path)}
            />
          )}
        </ReactModal>
      )}
    </div>
  )
}

export const QrRequestModal: React.FC<QrRequestModalProps> = (props) => {
  const { eventId, secretCode, handleModalClose, setIsActiveQrRequest, type } = props

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { addToast } = useToasts()

  const handleQrRequestSubmit = async (
    values: QrRequestFormikValues,
    actions: FormikActions<QrRequestFormikValues>,
  ) => {
    setIsSubmitting(true)
    const { requested_codes, secret_code } = values

    if (eventId) {
      await postRedeemRequests(eventId, requested_codes, secret_code, type)
        .then((_) => {
          setIsSubmitting(false)

          addToast('POAPs requested successfully', { appearance: 'success', autoDismiss: true })
          analyticsTracking.drop.trackRequestedCreateDropSuccess(eventId)
          setIsActiveQrRequest(eventId)
          handleModalClose()
        })
        .catch((e) => {
          console.log(e)
          setIsSubmitting(false)

          const isAdmin = authClient.isAuthenticated()
          const errorMessage = getErrorMessageFromCode(e.message, isAdmin)

          actions.setErrors({ secret_code: errorMessage })
          actions.setSubmitting(false)
        })
    }
  }

  const handleQrRequestModalClosing = () => handleModalClose()

  const warning = [
    <div key="warning" className={'backoffice-tooltip'}>
      Be sure to complete the 6 digit <b>Edit Code</b> that was originally used
    </div>,
  ]

  const editLabel = (
    <>
      <b>Edit Code</b>
      {/* @ts-ignore TODO: remove ignore TS validation */}
      <Tooltip content={warning}>
        <img alt="Informative message" src={infoButton} className={'info-button'} />
      </Tooltip>
    </>
  )

  return (
    <Formik
      initialValues={{
        requested_codes: 0,
        secret_code: secretCode ? secretCode : '',
      }}
      validationSchema={PoapQrRequestSchema}
      validateOnBlur={true}
      validateOnChange={false}
      onSubmit={handleQrRequestSubmit}
    >
      {({ handleSubmit, dirty, isValid }) => {
        return (
          <div className={'update-modal-container authentication_modal_container'}>
            <div className="select-container">
              <div className="bk-form-row">
                <EventField type="number" disabled={false} title={'Requested Mint Links'} name="requested_codes" />
              </div>
              <div className="bk-form-row">
                <EventField disabled={false} title={editLabel} name="secret_code" />
              </div>
            </div>
            <div className="modal-content">
              <div className="modal-buttons-container creation-modal">
                <SubmitButton
                  text="Cancel"
                  isSubmitting={false}
                  canSubmit={true}
                  onClick={handleQrRequestModalClosing}
                />
                <SubmitButton
                  text="Request"
                  isSubmitting={isSubmitting}
                  canSubmit={!isSubmitting && dirty && isValid}
                  onClick={handleSubmit}
                />
              </div>
            </div>
          </div>
        )
      }}
    </Formik>
  )
}

const DayPickerContainer = ({
  text,
  dayToSetup,
  handleDayClick,
  setFieldValue,
  placeholder = '',
  disabledDays,
  disabled,
  value,
  helpText,
}: DatePickerContainerProps) => {
  const handleDayChange = (day: Date) => handleDayClick(day, dayToSetup, setFieldValue)
  let _value = value

  if (value && isValid(value)) {
    const offset = new Date().getTimezoneOffset()
    const offsetSign = offset < 0 ? -1 : 1
    _value = new Date(Number(value) + offset * 60 * 1000 * offsetSign)
  }

  function parseDate(str: string, format: string) {
    const parsed = parse(str, format, new Date())
    if (isValid(parsed)) {
      return parsed
    }
  }

  let formatText = [<></>]
  if (helpText) {
    formatText = [
      <div key="formatText" className={'backoffice-tooltip'}>
        <span>{helpText}</span>
      </div>,
    ]
  }

  return (
    <div className={`date-picker-container ${dayToSetup === 'end_date' ? 'end-date-overlay' : ''}`}>
      <label className={'backoffice-tooltip-label'}>
        <span>{text}</span>
        {helpText && (
          <span>
            {/* @ts-ignore TODO: remove ignore TS validation */}
            <Tooltip content={formatText}>
              <img alt="Informative message" src={infoButton} className={'info-button'} />
            </Tooltip>
          </span>
        )}
      </label>
      <DayPickerInput
        placeholder={placeholder}
        dayPickerProps={{ disabledDays }}
        onDayChange={handleDayChange}
        formatDate={(date, format) => dateFormat(date, format)}
        format="yyyy-MM-dd"
        parseDate={parseDate}
        value={_value ? dateFormat(Number(_value), 'yyyy-MM-dd') : ''}
        inputProps={{ readOnly: 'readonly', disabled: disabled }}
      />
      <ErrorMessage name={dayToSetup} component="p" className="bk-error" />
    </div>
  )
}

export const EventField: React.FC<EventFieldProps> = ({
  title,
  name,
  disabled = false,
  type,
  placeholder,
  maxLength,
}) => {
  const hasErrors = (form: FormikProps<any>) => {
    // Field is not an array - Field does have errors
    const fieldNotArrayWithErrors = !!form.errors[name]
    if (fieldNotArrayWithErrors) return true

    // Field is an array
    const fieldIsArray = name.includes('[') && name.includes(']')

    if (fieldIsArray) {
      const arrayFieldName = name.split('[')[0]
      const arrayErrors = form.errors[arrayFieldName] as FormikErrors<any>

      // Field does have errors
      if (arrayErrors) {
        // Get field index from name
        const firstSquareBracket = name.indexOf('[')
        const lastSquareBracket = name.lastIndexOf(']')
        const currentErrorIndex = name.substring(firstSquareBracket + 1, lastSquareBracket)

        // Get error index from errors
        const currentElemInArrayExists = arrayErrors[currentErrorIndex]

        // Return true only if element index matches errors index
        return !!currentElemInArrayExists
      }
    }

    return false
  }

  const [count, setCount] = React.useState(0)
  const countCharacters = (field: { value: { length: number } }) => {
    setCount(field.value.length)
  }

  return (
    <Field
      name={name}
      render={({ field, form }: FieldProps) => (
        <>
          {type === 'textarea-character-count' && countCharacters(field)}
          <div className="bk-form-row">
            <label>{title}</label>
            {type === 'textarea' && (
              <div>
                <textarea
                  {...field}
                  wrap="soft"
                  disabled={disabled}
                  className={classNames(hasErrors(form) && 'error')}
                  placeholder={placeholder}
                />
              </div>
            )}
            {type === 'textarea-character-count' && (
              <div>
                <textarea
                  {...field}
                  wrap="soft"
                  disabled={disabled}
                  className={classNames((hasErrors(form) || count > 1500) && 'error')}
                  placeholder={placeholder}
                />
                <span
                  className={classNames('bk-character-count', count > 1500 && 'bk-character-count__error')}
                >{`${count}/1500`}</span>
              </div>
            )}
            {type !== 'textarea' && type !== 'textarea-character-count' && (
              <input
                {...field}
                placeholder={placeholder ? placeholder : ''}
                type={type || 'text'}
                disabled={disabled}
                className={classNames(hasErrors(form) && 'error')}
              />
            )}
            <ErrorMessage name={name} component="p" className="bk-error" />
          </div>
        </>
      )}
    />
  )
}

const CheckboxField: React.FC<EventFieldProps> = ({ title, action, checked }) => {
  return (
    <div className={'checkbox-field'} onClick={action}>
      <input type="checkbox" checked={checked} readOnly />
      <label>{title}</label>
    </div>
  )
}

export const EventList: React.FC = () => {
  const [criteria, setCriteria] = useState<string>('')
  const [offset, setOffset] = useState<number>(0)
  const [limit, setLimit] = useState<number>(10)
  const [orderBy, setOrderBy] = useState<SortCondition>({
    sort_by: 'id',
    sort_direction: SortDirection.descending,
  })

  let delayedId: NodeJS.Timeout

  const fetchEvents = useCallback(() => {
    let filter: EventFilter = {
      name: criteria ? criteria : undefined,
    }

    let canSeePrivateEvents = authClient.isAuthenticated()
    if (!canSeePrivateEvents) {
      filter = { ...filter, private_event: false }
    }

    return getPaginatedEvents(filter, offset, limit, orderBy)
  }, [offset, criteria, limit, orderBy])

  const [paginatedEvent, isLoadingPaginatedEvents, hasErrorPaginatedEvents] = useAsync<PaginatedEvent>(fetchEvents)

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target

    if (delayedId) {
      clearTimeout(delayedId)
    }

    delayedId = setTimeout(() => {
      setCriteria(value.toLowerCase())
      setOffset(0)
    }, 500)
  }

  const handleLimitChange = (value: number) => {
    setLimit(value)
    setOffset(0)
  }

  return (
    <div className={'bk-container'}>
      <Helmet>
        <title>POAP - Events</title>
        <meta property="og:title" content="POAP - Events" />
      </Helmet>
      <h2>Drops</h2>
      <div className="event-top-bar-container">
        <div className="left_content">
          <input type="text" placeholder="Search by name" onChange={handleNameChange} />
        </div>
        <div className="right_content">
          <Link to="/backoffice/events/new">
            <FilterButton text="Create new POAP" />
          </Link>
        </div>
      </div>
      <div className={'secondary-filters'}>
        <ResultsPerPage options={[10, 100, 1000]} selectedOption={10} onChange={handleLimitChange} />
      </div>
      {isLoadingPaginatedEvents && <Loading />}

      {hasErrorPaginatedEvents && <div>There was a problem fetching events</div>}

      {paginatedEvent && (
        <EventTable
          events={paginatedEvent.items}
          total={paginatedEvent.total}
          limit={paginatedEvent.limit}
          onChangePage={(page) => setOffset(page * paginatedEvent.limit)}
          onChangeSort={setOrderBy}
        />
      )}
    </div>
  )
}

const renderEventName = (event_name: string, event_url?: string) => {
  if (!isEmptyString(event_url)) {
    return (
      <a href={event_url} title={event_name} target="_blank" rel="noopener noreferrer">
        {event_name}
      </a>
    )
  }
  return <span>{event_name}</span>
}

const EventTable: React.FC<EventTableProps> = ({ events, total, onChangePage, onChangeSort, limit }) => {
  const [page, setPage] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<SortCondition>({
    sort_by: 'id',
    sort_direction: SortDirection.descending,
  })

  useEffect(() => {
    setPage(0)
  }, [total])

  const isAdmin = authClient.isAuthenticated()

  const handlePageChange = (obj: PaginateAction) => {
    const pageSel = obj.selected

    setPage(pageSel)
    onChangePage(pageSel)
  }

  const handleSort = (field: string) => {
    let dir

    if (orderBy.sort_by === field && orderBy.sort_direction === SortDirection.ascending) {
      dir = SortDirection.descending
    } else {
      dir = SortDirection.ascending
    }
    const sortCond = {
      sort_by: field,
      sort_direction: dir,
    }

    setOrderBy(sortCond)
    onChangeSort(sortCond)
  }

  return (
    <div>
      <div className={'admin-table transactions'}>
        <div className={'row table-header visible-md'}>
          <div className={'col-md-1 center pointer'} onClick={() => handleSort('id')}>
            #
            {orderBy.sort_by === 'id' && (
              <img
                className={'img-sort'}
                src={orderBy.sort_direction === SortDirection.ascending ? sortUp : sortDown}
                alt={'sort'}
              />
            )}
          </div>
          <div className={`col-md-6 pointer`} onClick={() => handleSort('name')}>
            Name of the POAP
            {orderBy.sort_by === 'name' && (
              <img
                className={'img-sort'}
                src={orderBy.sort_direction === SortDirection.ascending ? sortUp : sortDown}
                alt={'sort'}
              />
            )}
          </div>
          <div className={'col-md-2 center'} onClick={() => handleSort('start_date')}>
            Start Date
            {orderBy.sort_by === 'start_date' && (
              <img
                className={'img-sort'}
                src={orderBy.sort_direction === SortDirection.ascending ? sortUp : sortDown}
                alt={'sort'}
              />
            )}
          </div>
          <div className={'col-md-2 center'}>Image</div>
          {isAdmin && <div className={'col-md-1 center'}>Edit</div>}
        </div>
        <div className={'admin-table-row'}>
          {events &&
            events.map((event, i) => (
              <div className={`row ${i % 2 === 0 ? 'even' : 'odd'} relative`} key={event.id}>
                <div className={'col-md-1 center'}>
                  <span className={'visible-sm visible-md'}>#</span>
                  {event.id}
                </div>
                <div className={`col-md-6 ellipsis`}>
                  <span className={'visible-sm'}>
                    Name of the POAP: <br />
                  </span>
                  {renderEventName(event.name, event.event_url)}
                </div>
                <div className={'col-md-2 center'}>
                  <span className={'visible-sm'}>Start date: </span>
                  <span>{event.start_date}</span>
                </div>
                <div className={'col-md-2 center '}>
                  {/* @ts-ignore TODO: remove ignore TS validation */}
                  <Tooltip
                    content={[
                      <div key={event.id} className={'event-table-tooltip'}>
                        <img alt={event.image_url} src={event.image_url} className={'tooltipped'} />
                      </div>,
                    ]}
                  >
                    <img alt={event.image_url} className={'logo-image'} src={event.image_url} />
                  </Tooltip>
                </div>
                <div className={'col-md-1 center event-edit-icon-container'}>
                  <Link to={`/backoffice/events/${event.fancy_id}`}>
                    <EditIcon />
                  </Link>
                </div>
              </div>
            ))}
        </div>
        <div className={'pagination'}>
          {total && total > limit && (
            <ReactPaginate
              pageCount={Math.ceil(total / limit)}
              marginPagesDisplayed={2}
              pageRangeDisplayed={limit}
              forcePage={page}
              activeClassName={'active'}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}
